import { useEffect, useMemo, useRef, useState } from 'react'
import { Camera } from 'lucide-react'

// ช่องรูป (โปรไฟล์/โลโก้) — เลือกไฟล์แล้ว "ยังไม่อัปโหลด": ส่ง File ให้ onChange เก็บไว้ แล้วหน้านั้นอัปโหลดจริงตอนกดปุ่มบันทึก (uploadImage)
// value: URL เดิม หรือ File ที่เลือกไว้ · onChange('') = ลบรูป
interface Props {
  label: string
  value: string | File | null
  fallback: string
  round?: boolean
  onChange: (value: string | File) => void
}

const MAX_BYTES = 10 * 1024 * 1024   // ต้องตรงกับ RULES["IMAGE"] ใน API (อัปโหลดตรง) — API เก่าที่ยังไม่มีอัปโหลดตรงรับ 2 MB และจะแจ้งเอง

export default function ImageUploader({ label, value, fallback, round, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState('')
  const radius = round ? 'rounded-full' : 'rounded-[8px]'
  const pending = value instanceof File ? value : null

  const previewUrl = useMemo(() => (pending ? URL.createObjectURL(pending) : null), [pending])
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }, [previewUrl])
  const src = pending ? previewUrl : (value as string | null)

  const handleFile = (file: File | undefined) => {
    if (!file) return
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) return setError('รองรับเฉพาะไฟล์ PNG, JPG หรือ WEBP')
    if (file.size > MAX_BYTES) return setError('ไฟล์ต้องมีขนาดไม่เกิน 10 MB')
    setError('')
    onChange(file)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="flex items-center gap-4">
      <div className={`relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden bg-accent/10 text-2xl font-semibold text-accent ${radius}`}>
        {src ? <img src={src} alt={label} className="h-full w-full object-cover" /> : fallback}
      </div>
      <div>
        <p className="text-sm font-medium text-[#19172A]">{label}</p>
        <div className="mt-1 flex items-center gap-3">
          <button type="button" onClick={() => inputRef.current?.click()}
            className="flex items-center gap-1.5 text-sm font-medium text-accent hover:underline">
            <Camera className="h-3.5 w-3.5" />
            {src ? 'เปลี่ยนรูป' : 'เลือกรูป'}
          </button>
          {src && (
            <button type="button" onClick={() => onChange('')} className="text-sm text-rose-500 hover:underline">ลบรูป</button>
          )}
        </div>
        <p className="text-xs text-[#69738A]">
          {pending ? <span className="text-amber-600">รูปใหม่ — จะอัปโหลดเมื่อกดบันทึก</span> : 'PNG, JPG หรือ WEBP ไม่เกิน 2 MB'}
        </p>
        {error && <p className="text-xs text-rose-500">{error}</p>}
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])} />
      </div>
    </div>
  )
}
